import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
// @ts-ignore - TypeScript casing issue
import { useRecoilState } from 'recoil';
import { submitPronunciation, getPronunAudio } from '../../../services/practiceApi';
import { getUniversalLearningId } from './utils/audioUtils';
import { useAudioRecording } from './hooks/useAudioRecording';
import { useSpeechSynthesis } from './hooks/useSpeechSynthesis';
// @ts-ignore - TypeScript casing issue
import { speakingAudioRecordingsState } from '../../../recoil/atoms';
import OverallScoreCard from './components/OverallScoreCard';
import RecordedAudioPlayer from './components/RecordedAudioPlayer';
import WordAnalysis from './components/WordAnalysis';
import AudioRecorder from './components/AudioRecorder';
// Map raw server-side errors (e.g. /tmp file missing) to user-friendly messages
const sanitizeSubmissionError = (error) => {
  const msg = typeof error === 'string' ? error : (error?.message || '');
  if (
    msg.includes('/tmp/') ||
    msg.includes('No such file or directory') ||
    msg.includes('ring-multipart')
  ) {
    return 'We had trouble processing your recording. Please make sure your microphone is working properly and try again.';
  }
  return msg || 'Failed to submit pronunciation.';
};

const Prononciation = ({ question, onAnswer, onNext, isLastQuestion = false, sessionData = null, langCode = 'de' }) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [submissionError, setSubmissionError] = useState(null);
  const [audioRecordings, setAudioRecordings] = useRecoilState(speakingAudioRecordingsState);
  // Track semua blob URLs yang dibuat untuk cleanup
  const blobUrlsRef = useRef([]);
  const lastSubmitBlobRef = useRef(null);
  const lastSubmitDurationRef = useRef(0);
  // changed by Cursor: simpan base64 audioUrl untuk di-pass ke onAnswer (agar survive navigation state)
  const base64AudioUrlRef = useRef(null);
  // Audio player untuk pronunciation audio dari API
  const pronunAudioRef = useRef(null);
  const [isLoadingPronunAudio, setIsLoadingPronunAudio] = useState(false);
  const [isPlayingPronunAudio, setIsPlayingPronunAudio] = useState(false);
  // pronunAudioUrl is set but not read - kept for potential future use or debugging
  // eslint-disable-next-line no-unused-vars
  const [pronunAudioUrl, setPronunAudioUrl] = useState(null);

  const recording = useAudioRecording();
  const effectiveLangCode = sessionData?.['lang-code'] || langCode || 'de';
  const speech = useSpeechSynthesis(effectiveLangCode);

  const textToRead = question.text || question['text-soal'] || question.question || question['text-to-read'] || '';
  const translation = question.translation || question['translation'] || '';
  // Extract questionId from multiple possible sources to ensure consistency
  // CRITICAL: soal-id is required for matching audio in review page
  const questionId = question['soal-id'] || question.soalId || question.id || question.originalQuestion?.['soal-id'] || question.originalQuestion?.soalId || question.originalQuestion?.id || null;
  const taskIndex = (question.number - 1).toString(); // Convert to string for storage key
  
  // Debug: Log question info on mount/render
  useEffect(() => {
    console.log('🔍 Prononciation - Component rendered:', {
      questionNumber: question.number,
      taskIndex,
      questionId,
      questionSoalId: question['soal-id'],
      questionIdField: question.id,
      originalQuestionSoalId: question.originalQuestion?.['soal-id']
    });
  }, [question.number, taskIndex, questionId]);
  
  // Warn if questionId is missing (critical for review page matching)
  if (!questionId) {
    console.warn('⚠️ Prononciation - No soal-id found for question:', {
      questionNumber: question.number,
      questionKeys: Object.keys(question),
      questionSoalId: question['soal-id'],
      questionId: question.id,
      originalQuestion: question.originalQuestion
    });
  }

  // changed by Cursor: removed localStorage audio loading (audio now lives in Recoil only)
  // Load saved assessment from localStorage (assessment is small, OK for localStorage)
  useEffect(() => {
    const savedAssessmentKey = `pronunciation-assessment-${questionId}`;
    const savedAssessmentData = localStorage.getItem(savedAssessmentKey);
    
    if (savedAssessmentData) {
      try {
        setAssessmentResult(JSON.parse(savedAssessmentData));
      } catch (error) {
        console.error('Error parsing saved assessment:', error);
      }
    }
  }, [questionId]);

  // Reset when question changes (if no saved assessment)
  useEffect(() => {
    const savedAssessmentKey = `pronunciation-assessment-${questionId}`;
    const savedAssessmentData = localStorage.getItem(savedAssessmentKey);
    
    if (!savedAssessmentData) {
      recording.reset();
      setAssessmentResult(null);
      setSubmissionError(null);
      speech.reset();
    }
    
    // Cleanup audio when question changes
    if (pronunAudioRef.current) {
      pronunAudioRef.current.pause();
      pronunAudioRef.current.currentTime = 0;
      setIsLoadingPronunAudio(false);
      setIsPlayingPronunAudio(false);
    }
    setPronunAudioUrl(null);
  }, [questionId]);

  // Get audio URL from Recoil (preferred) or use recording state
  const audioUrlForPlayback = audioRecordings[taskIndex]?.audioUrl || recording.recordedAudioUrl;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      recording.cleanup();
      speech.cleanup();
      // Cleanup semua blob URLs yang pernah dibuat (from AudioRecorder hook)
      blobUrlsRef.current.forEach(url => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // Ignore errors saat revoke
        }
      });
      blobUrlsRef.current = [];
    };
  }, []);

  const handleSubmit = async (audioBlob, durationMs, audioDataUrl) => {
    if (!audioBlob) return;

    setIsSubmitting(true);

    try {
      const universalLearningId = getUniversalLearningId();
      
      if (!universalLearningId) {
        // No learner-id - redirect to login
        console.log('❌ No learner-id found, redirecting to login...');
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
        localStorage.removeItem('universal-learning-id');
        navigate('/learner/login');
        return;
      }

      // changed by Cursor: prioritize lesson-id for submission payload
      const lessonId = sessionData?.['lesson-id'] || question['lesson-id'] || null;
      const subcourseId = lessonId ? null : (sessionData?.['subcourse-id'] || question['subcourse-id'] || null);
      const courseId = (lessonId || subcourseId) ? null : (sessionData?.['course-id'] || question['course-id'] || null);
      const sessionId = sessionData?.['session-id'] || question['session-id'] || null;
      const prosetId = question['proset-id'] || question.prosetId || question['proset_id'] || null;
      const soalId = question['soal-id'] || question.id || null;
      const langCode = sessionData?.['lang-code'] || null;

      const result = await submitPronunciation({
        lessonId,
        courseId,
        subcourseId,
        sessionId,
        prosetId,
        soalId,
        textToRead,
        duration: durationMs,
        universalLearningId,
        audioBlob,
        langCode
      });

      if (result.success) {
        setAssessmentResult(result.data);
        
        // Save only assessment result to localStorage (audio too large for localStorage)
        // CRITICAL: Ensure questionId is available before saving
        if (!questionId) {
          console.error('❌ Prononciation - Cannot save assessment: questionId is null!', {
            questionNumber: question.number,
            questionKeys: Object.keys(question),
            questionSoalId: question['soal-id'],
            questionId: question.id,
            originalQuestion: question.originalQuestion
          });
        } else {
          const savedAssessmentKey = `pronunciation-assessment-${questionId}`;
          console.log('💾 Prononciation - Saving assessment to localStorage:', {
            questionId,
            assessmentKey: savedAssessmentKey,
            hasAssessmentData: !!result.data,
            assessmentKeys: result.data ? Object.keys(result.data) : []
          });
          
          try {
            localStorage.setItem(savedAssessmentKey, JSON.stringify(result.data));
            console.log('✅ Prononciation - Assessment saved successfully to localStorage');
          } catch (error) {
            console.error('❌ Error saving assessment to localStorage:', error);
            // If quota exceeded, try to clear old assessments
            if (error.name === 'QuotaExceededError') {
              console.warn('⚠️ localStorage quota exceeded, clearing old pronunciation assessments...');
              // Clear all old pronunciation assessments
              Object.keys(localStorage).forEach(key => {
                if (key.startsWith('pronunciation-assessment-')) {
                  localStorage.removeItem(key);
                }
              });
              // Try again
              try {
                localStorage.setItem(savedAssessmentKey, JSON.stringify(result.data));
                console.log('✅ Prononciation - Assessment saved after cleanup');
              } catch (retryError) {
                console.error('❌ Still failed after cleanup:', retryError);
              }
            }
          }
        }
        
        if (onAnswer) {
          onAnswer({
            questionId: question['soal-id'] || question.id,
            prosetId: question['proset-id'] || question.prosetId,
            type: question.type || question['question-type'] || 'pronunciation',
            answer: audioBlob,
            userAnswer: textToRead, // Use question text as answer
            // changed by Cursor: include base64 audioUrl supaya survive navigation state ke review page
            // localStorage bisa gagal karena quota/size limit, jadi pass langsung lewat props
            audioUrl: base64AudioUrlRef.current || null,
            duration: durationMs, // Already in milliseconds
            assessment: result.data,
            isCorrect: false
          });
        }
      } else {
        throw new Error(result.message || 'Failed to submit pronunciation');
      }
    } catch (error) {
      console.error('Error submitting pronunciation:', error);
      
      // Check if error is about learner-id - interceptor will handle redirect
      const errorMessage = error?.message || '';
      const errorMessageLower = errorMessage.toLowerCase();
      const isLearnerIdError = errorMessageLower.includes('learner-id') || 
                               errorMessageLower.includes('learner id') ||
                               errorMessageLower.includes('learner-id is required') ||
                               errorMessageLower.includes('learner-id required') ||
                               errorMessageLower.includes('learner-id missing') ||
                               errorMessageLower.includes('must provide learner-id');
      
      if (isLearnerIdError) {
        // Interceptor will handle redirect, just log here
        console.log('❌ Submit failed due to learner-id error');
        return;
      }
      
      setSubmissionError(sanitizeSubmissionError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Convert Blob to Base64 for localStorage
  const convertBlobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result); // data:audio/wav;base64,...
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // changed by Cursor: save base64 audio to Recoil (replaces localStorage - no quota limits, survives SPA navigation)
  const handleRecordingComplete = async (wavBlob, durationMs, audioDataUrl, blobUrl) => {
    // Track blob URL untuk cleanup
    if (blobUrl && blobUrl.startsWith('blob:')) {
      blobUrlsRef.current.push(blobUrl);
    }
    
    // Save base64 audio to Recoil (survives SPA navigation, no quota limits)
    try {
      const base64String = await convertBlobToBase64(wavBlob);
      // Keep base64 ref for onAnswer (survive navigation state)
      base64AudioUrlRef.current = base64String;
      
      setAudioRecordings(prev => ({
        ...prev,
        [taskIndex]: {
          audioUrl: base64String,
          soalId: questionId,
        },
      }));
      console.log('✅ Prononciation - Saved audio to Recoil:', { taskIndex, questionId });
    } catch (error) {
      console.error('❌ Prononciation - Error saving audio to Recoil:', error);
    }

    // Store for retry
    lastSubmitBlobRef.current = wavBlob;
    lastSubmitDurationRef.current = durationMs;

    // Submit to API
    handleSubmit(wavBlob, durationMs, audioDataUrl);
  };

  const handleRerecord = () => {
    setSubmissionError(null);
    setAssessmentResult(null);
    lastSubmitBlobRef.current = null;
    lastSubmitDurationRef.current = 0;
    base64AudioUrlRef.current = null;
    recording.reset();
    // Remove saved audio for this question from Recoil
    setAudioRecordings(prev => {
      const next = { ...prev };
      delete next[taskIndex];
      return next;
    });
  };

  const overallScores = assessmentResult?.['overall-scores'] || {};
  const wordDetails = assessmentResult?.['word-details'] || [];

  // Get prosetId from question or sessionData as fallback
  const prosetId = question['proset-id'] || question.prosetId || question.proset_id || 
                   sessionData?.['proset-id'] || sessionData?.prosetId || null;
  const soalId = question['soal-id'] || question.soalId || question.id || null;

  // Handle play full sentence using API audio
  const handlePlayFullSentence = async () => {
    if (!textToRead || !textToRead.trim()) {
      console.warn('⚠️ No text to read');
      return;
    }

    // If already playing (API audio), stop it
    if (pronunAudioRef.current && !pronunAudioRef.current.paused) {
      pronunAudioRef.current.pause();
      pronunAudioRef.current.currentTime = 0;
      setIsLoadingPronunAudio(false);
      setIsPlayingPronunAudio(false);
      return;
    }

    // If browser TTS is playing, stop it first
    if (speech.isPlayingFullSentence) {
      speech.playFullSentence(textToRead); // This will stop if already playing
      return;
    }

    setIsLoadingPronunAudio(true);

    try {
      // Fetch or generate audio from API
      const result = await getPronunAudio(textToRead, effectiveLangCode);
      
      if (result.success && result.data?.['stream-url']) {
        const audioUrl = result.data['stream-url'];
        setPronunAudioUrl(audioUrl);
        
        // Create audio element if not exists
        if (!pronunAudioRef.current) {
          pronunAudioRef.current = new Audio();
        }
        
        // Set up audio element
        pronunAudioRef.current.src = audioUrl;
        pronunAudioRef.current.onended = () => {
          setIsPlayingPronunAudio(false);
        };
        pronunAudioRef.current.onpause = () => {
          setIsPlayingPronunAudio(false);
        };
        pronunAudioRef.current.onerror = (error) => {
          console.error('❌ Error playing pronunciation audio:', error);
          setIsLoadingPronunAudio(false);
          setIsPlayingPronunAudio(false);
          // Fallback to browser TTS
          speech.playFullSentence(textToRead);
        };
        
        // Play audio
        setIsLoadingPronunAudio(false);
        await pronunAudioRef.current.play();
        setIsPlayingPronunAudio(true);
        console.log('🔊 Playing pronunciation audio from API:', audioUrl);
      } else {
        console.warn('⚠️ Failed to get pronunciation audio, falling back to browser TTS');
        // Fallback to browser TTS
        speech.playFullSentence(textToRead);
        setIsLoadingPronunAudio(false);
      }
    } catch (error) {
      console.error('❌ Error fetching pronunciation audio:', error);
      // Fallback to browser TTS
      speech.playFullSentence(textToRead);
      setIsLoadingPronunAudio(false);
      setIsPlayingPronunAudio(false);
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (pronunAudioRef.current) {
        pronunAudioRef.current.pause();
        pronunAudioRef.current = null;
      }
      setIsPlayingPronunAudio(false);
    };
  }, []);

  return (
    <motion.div
      className="flex flex-col gap-5 max-md:gap-4 max-md:pb-10"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="flex justify-between items-center pb-4 border-b-2 border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)] max-md:flex-col max-md:items-start max-md:gap-3">
        <motion.span
          className="px-3.5 py-2 bg-gradient-to-br from-[#12766d] to-[#236463] text-white rounded-lg text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap shrink-0 max-md:text-[10px] max-md:px-2 max-md:py-1"
          initial={{ scale: 0.85 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          Pronunciation
        </motion.span>
      </div>

      <div className="flex flex-col gap-5">
        <div className="mb-0">
          <h2 className="m-0 text-xl font-medium text-[var(--text-primary)] leading-relaxed max-md:text-lg">
            {textToRead}
          </h2>

          {translation && (
            <>
              <div className="h-px bg-[var(--border-color)] my-4" />
              <div>
                <div className="text-sm font-semibold text-[var(--text-primary)] mb-2">
                  Translation:
                </div>
                <p className="m-0 text-base text-[var(--text-secondary)] italic">
                  {translation}
                </p>
              </div>
            </>
          )}
        </div>

        {assessmentResult && (
          <div className="mt-0 flex flex-col gap-4 max-md:gap-3">
            <OverallScoreCard score={overallScores['accuracy-score']} />
            {onNext && (
              <div className="flex justify-center my-3">
                <button
                  type="button"
                  className="py-2 px-4 text-sm font-semibold rounded-lg border-none cursor-pointer transition-all duration-300 bg-[#fbbf24] text-[var(--text-primary)] dark:text-[#1d4746] hover:bg-[#f59e0b] hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(251,191,36,0.3)] min-w-0"
                  onClick={onNext}
                >
                  {isLastQuestion ? 'Submit' : 'Next Question'}
                </button>
              </div>
            )}
            <RecordedAudioPlayer audioUrl={audioUrlForPlayback} />
            <WordAnalysis
              wordDetails={wordDetails}
              textToRead={textToRead}
              onPlayFullSentence={handlePlayFullSentence}
              onPlayWord={speech.playWord}
              isPlayingFullSentence={isPlayingPronunAudio || speech.isPlayingFullSentence}
              isLoadingFullSentence={isLoadingPronunAudio}
              playingWordIndex={speech.playingWordIndex}
              loadingWordIndex={speech.loadingWordIndex}
            />
          </div>
        )}

        <div className="mt-1 flex flex-col items-center gap-5 max-md:mt-2 max-md:gap-4">
          {!assessmentResult && (
            <AudioRecorder
              isRecording={recording.isRecording}
              isSubmitting={isSubmitting}
              recordingDuration={recording.recordingDuration}
              onStartRecording={() => recording.startRecording(handleRecordingComplete)}
              onStopRecording={recording.stopRecording}
              analyserNode={recording.analyserRef}
              recordingError={recording.recordingError}
            />
          )}

          {submissionError && !isSubmitting && (
            <div className="w-full flex flex-col items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
              <p className="text-sm text-red-600 dark:text-red-400 font-medium text-center m-0">{submissionError}</p>
              <button
                type="button"
                className="py-2 px-4 text-sm font-semibold rounded-lg border-none cursor-pointer transition-all duration-300 bg-gradient-to-br from-[#12766d] to-[#0f5d56] text-white hover:shadow-[0_4px_12px_rgba(18,118,109,0.3)] hover:-translate-y-0.5"
                onClick={handleRerecord}
              >
                Re-record
              </button>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default Prononciation;
